import { useMemo } from 'react';

import { Alert, AlertDescription, Button, Input } from '@parrot-software/design-system';
import { LoaderCircle, ShieldCheck, XCircle } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { Trans, useTranslation } from 'react-i18next';

import { IDENTITY_ERROR_CODES } from '@identity/application/errors';
import { FieldLabel, InfoNote, StepCard } from '@identity/presentation/components/base';
import { useVerificationLinkSender } from '@identity/presentation/hooks';
import { identityErrorKey, translateIdentityError } from '@identity/presentation/utils';

import { createEmailFormSchema } from './EmailStep.schema';
import type { EmailFormValues } from './EmailStep.schema';

/**
 * The one send failure that also marks the field: the email is unique, so its conflict is a fact
 * about the address, even though the sentence reads in the banner with the rest of the failures.
 *
 * It does not enable user enumeration here, because whoever sees it is inside their own session and
 * can only try one address at a time against the rate limit.
 */
const EMAIL_IN_USE_ERROR_KEY = identityErrorKey(IDENTITY_ERROR_CODES.EMAIL_IN_USE);

export type EmailCaptureViewProps = {
  /** The address the account has today. It preloads the field; it is not a placeholder. */
  accountEmail: string;
  onSent: (email: string) => void;
  /** Already translated. Absent means this flow does not number its steps. */
  stepLabel?: string;
};

/**
 * Capture view of the email step.
 *
 * The field is editable with no condition and there is no flag for it: this view only renders while
 * the requirement is pending, so the derivation is what enforces "editable only until verified".
 *
 * The address comes from the account status and never from the login form, because this screen can
 * mount on a later reload where that form no longer exists.
 *
 * @param {EmailCaptureViewProps} props - Component properties
 */
export const EmailCaptureView = ({ accountEmail, onSent, stepLabel }: EmailCaptureViewProps) => {
  const { t } = useTranslation('identity');
  const { errorKey, errorRetryAfterSeconds, isSending, send } = useVerificationLinkSender();
  const resolver = useMemo(() => (yupResolver(createEmailFormSchema()) as unknown) as Resolver<EmailFormValues>, []);

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<EmailFormValues>({
    defaultValues: { email: accountEmail },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver,
  });

  const email = watch('email');
  const validationErrorKey = errors.email?.message;
  const hasFieldError = Boolean(validationErrorKey) || errorKey === EMAIL_IN_USE_ERROR_KEY;

  const { onChange: onFieldChange, ...emailField } = register('email');

  const onSubmit = handleSubmit(async (values) => {
    const result = await send({ channel: 'EMAIL', value: values.email });

    if (result) onSent(values.email);
  });

  return (
    <StepCard
      description={t('verifyAccount.email.subtitle')}
      icon={
        <span className="flex size-8 items-center justify-center rounded-full bg-bg-info">
          <ShieldCheck aria-hidden className="size-4 text-info" />
        </span>
      }
      stepLabel={stepLabel}
      title={t('verifyAccount.email.title')}
    >
      <form className="flex flex-col gap-6 font-sans" noValidate onSubmit={onSubmit}>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="identity-verify-email">{t('verifyAccount.email.emailLabel')}</FieldLabel>
          <Input
            {...emailField}
            aria-invalid={hasFieldError}
            autoComplete="email"
            className={cn({ 'border-destructive focus-visible:ring-destructive': hasFieldError })}
            id="identity-verify-email"
            onChange={(event) => {
              if (validationErrorKey) clearErrors('email');

              return onFieldChange(event);
            }}
            type="email"
          />
          {validationErrorKey && (
            <p className="text-xs text-destructive" data-testid="email-field-error">
              {t(validationErrorKey)}
            </p>
          )}
        </div>

        <InfoNote>
          <Trans components={{ strong: <strong /> }} i18nKey="identity:verifyAccount.email.note" />
        </InfoNote>

        {errorKey && (
          <Alert
            className={cn(
              'border-destructive bg-destructive-foreground font-sans',
              '[&>svg]:top-1/2 [&>svg]:-translate-y-1/2 [&>svg+div]:translate-y-0',
            )}
            data-testid="email-send-error"
            variant="destructive"
          >
            <XCircle aria-hidden className="size-4" />
            <AlertDescription className="text-sm">
              {translateIdentityError(t, errorKey, errorRetryAfterSeconds)}
            </AlertDescription>
          </Alert>
        )}

        <Button className="w-full sm:w-fit" disabled={isSending || !email} type="submit">
          {isSending && (
            <LoaderCircle aria-hidden className="size-4 shrink-0 animate-spin" data-testid="email-send-spinner" />
          )}
          {t('verifyAccount.email.submit')}
        </Button>
      </form>
    </StepCard>
  );
};
